import Entity from "../../core/entity/Entity";
import { rUniform } from "../../core/util/Random";
import { LevelEntity } from "../LevelEntity";

// Sound parameters for different enemy sizes
const EXPLOSION_SETTINGS: Record<string, any> = {
  interceptor: {
    baseFrequency: 400,
    duration: 0.2,
    volume: 0.2,
    noiseGain: 0.3,
    filterFreq: 1500,
  },
  spiraler: {
    baseFrequency: 350,
    duration: 0.22,
    volume: 0.22,
    noiseGain: 0.35,
    filterFreq: 1350,
  },
  ghost: {
    baseFrequency: 320,
    duration: 0.25,
    volume: 0.24,
    noiseGain: 0.4,
    filterFreq: 1300,
  },
  zephyr: {
    baseFrequency: 800, // High pitched for value
    duration: 0.4,
    volume: 0.35,
    noiseGain: 0.2, // Less noise, more tone
    filterFreq: 2000, // Bright sound
  },
  fighter: {
    baseFrequency: 300,
    duration: 0.25,
    volume: 0.25,
    noiseGain: 0.4,
    filterFreq: 1200,
  },
  basic: {
    baseFrequency: 200,
    duration: 0.35,
    volume: 0.3,
    noiseGain: 0.5,
    filterFreq: 1000,
  },
  bomber: {
    baseFrequency: 120,
    duration: 0.5,
    volume: 0.4,
    noiseGain: 0.6,
    filterFreq: 800,
  },
  carrier: {
    baseFrequency: 80,
    duration: 0.7,
    volume: 0.5,
    noiseGain: 0.7,
    filterFreq: 600,
  },
  boss: {
    baseFrequency: 60,
    duration: 1.0,
    volume: 0.6,
    noiseGain: 0.8,
    filterFreq: 500,
  },
};

export default class EnemyDestroyedSoundGenerator
  extends LevelEntity
  implements Entity
{
  private audioContext: AudioContext;

  constructor() {
    super();
    this.tags = ["enemyDestroyedSoundGenerator"];
    this.id = "enemyDestroyedSoundGenerator";

    // We'll get the audio context from the game when added
    this.audioContext = null as any;
  }

  onAdd() {
    if (!this.game?.audio) {
      console.warn("No audio context available in game");
      return;
    }

    this.audioContext = this.game.audio;
  }

  /**
   * Handle enemy destroyed events
   */
  onEnemyDestroyed(eventData: { enemyType: string; points: number }) {
    if (!this.audioContext || !this.game?.masterGain) {
      return;
    }

    this.synthesizeExplosionSound(eventData.enemyType);
  }

  private synthesizeExplosionSound(enemyType: string) {
    const settings =
      EXPLOSION_SETTINGS[enemyType] || EXPLOSION_SETTINGS["basic"];
    const now = this.audioContext.currentTime;

    // Create main explosion components
    this.createExplosionBurst(now, settings);
    this.createExplosionTone(now, settings);
  }

  private createExplosionBurst(now: number, settings: any) {
    // White noise burst for explosion texture
    const bufferSize = this.audioContext.sampleRate * settings.duration;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate,
    );
    const data = buffer.getChannelData(0);

    // Generate white noise with envelope
    for (let i = 0; i < bufferSize; i++) {
      const progress = i / bufferSize;
      const envelope = Math.pow(1 - progress, 2); // Exponential decay
      data[i] = rUniform(-1, 1) * envelope;
    }

    // Create buffer source
    const noiseSource = this.audioContext.createBufferSource();
    noiseSource.buffer = buffer;

    // Filter for tonal shaping
    const noiseFilter = this.audioContext.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.setValueAtTime(settings.filterFreq, now);
    noiseFilter.Q.setValueAtTime(2, now);

    // Gain for volume control
    const noiseGain = this.audioContext.createGain();
    noiseGain.gain.setValueAtTime(settings.noiseGain, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + settings.duration);

    // Connect and play
    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.game!.masterGain);

    noiseSource.start(now);
    noiseSource.stop(now + settings.duration);

    // Cleanup
    noiseSource.addEventListener("ended", () => {
      noiseSource.disconnect();
      noiseFilter.disconnect();
      noiseGain.disconnect();
    });
  }

  private createExplosionTone(now: number, settings: any) {
    // Pitched tone for impact character
    const oscillator = this.audioContext.createOscillator();
    oscillator.type = "sawtooth"; // Aggressive sound

    // Filter for frequency shaping
    const filter = this.audioContext.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(settings.filterFreq, now);
    filter.frequency.exponentialRampToValueAtTime(
      settings.filterFreq * 0.3,
      now + settings.duration,
    );
    filter.Q.setValueAtTime(1, now);

    // Gain for volume envelope
    const gainNode = this.audioContext.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(settings.volume, now + 0.01); // Quick attack
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      now + settings.duration * 0.8,
    ); // Decay

    // Frequency sweep for explosion character
    const startFreq = settings.baseFrequency * 1.5;
    const endFreq = settings.baseFrequency * 0.5;

    oscillator.frequency.setValueAtTime(startFreq, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      endFreq,
      now + settings.duration * 0.6,
    );

    // Connect audio graph
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.game!.masterGain);

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + settings.duration);

    // Cleanup
    oscillator.addEventListener("ended", () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    });
  }
}
